import React, { createContext, useContext, useEffect, useState } from 'react';

// Toggleable product modules. V1 ships "paid-first" — eligibility + patient
// responsibility — with the Claims & Denials (retrospective RCM) context OFF by
// default. Flip it on to tell the full revenue-cycle story.
const LS_KEY = 'due_health_features';

const DEFAULTS = {
  claimsDenials: false,
  // Good-faith estimator — deferred for the pilot demo (off by default).
  estimator: false,
  // Prior-auth / authorization workflow — explicitly deferred for the pilot demo (3rd discussion).
  priorAuth: false,
};

function read() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) { /* ignore */ }
  return { ...DEFAULTS };
}

// Non-hook reader for modules that aren't React components (e.g. copilot.js).
export function claimsEnabled() {
  return read().claimsDenials === true;
}

const FeaturesContext = createContext(null);

export function FeaturesProvider({ children }) {
  const [features, setFeatures] = useState(read);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(features)); } catch (e) { /* ignore */ }
  }, [features]);

  const setFeature = (key, value) => setFeatures((f) => ({ ...f, [key]: value }));
  const toggle = (key) => setFeatures((f) => ({ ...f, [key]: !f[key] }));

  return (
    <FeaturesContext.Provider value={{ features, setFeature, toggle }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  const ctx = useContext(FeaturesContext);
  if (!ctx) throw new Error('useFeatures must be inside FeaturesProvider');
  return ctx;
}

// Convenience: just the Claims & Denials flag.
export function useClaimsEnabled() {
  return useFeatures().features.claimsDenials;
}

// Convenience: just the Estimator flag.
export function useEstimatorEnabled() {
  return useFeatures().features.estimator;
}

// Non-hook reader for modules that aren't React components (e.g. copilot.js).
export function estimatorEnabled() {
  return read().estimator === true;
}

// Convenience: just the prior-auth / authorization flag.
export function usePriorAuthEnabled() {
  return useFeatures().features.priorAuth;
}

// Non-hook reader for modules that aren't React components (e.g. lib/data.js).
export function priorAuthEnabled() {
  return read().priorAuth === true;
}
